import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  MessageSquare,
  FileText,
  AlertTriangle,
  Calculator,
  Send,
  User,
  Image,
  Download,
  ShieldAlert,
  Eye,
  Star,
  TrendingUp,
  Camera,
  Wallet,
  Percent
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingProjectDetail() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  // Get initial tab from URL params
  const initialTab = searchParams.get("tab") || "details";
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCounterOfferDialog, setShowCounterOfferDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState(initialTab);
  const [contractSubTab, setContractSubTab] = useState("holding");
  
  // Chat with client state
  const [chatMessage, setChatMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Team chat state
  const [teamMessages, setTeamMessages] = useState([]);
  const [newTeamMessage, setNewTeamMessage] = useState("");
  const [sendingTeamMessage, setSendingTeamMessage] = useState(false);
  const teamMessagesEndRef = useRef(null);
  const teamChatContainerRef = useRef(null);
  
  // Daily progress state
  const [dailyProgress, setDailyProgress] = useState([]);
  
  // Counter-offer state
  const [counterOfferData, setCounterOfferData] = useState({
    proposed_budget: 0,
    message: ""
  });
  const [isCounterOfferEdited, setIsCounterOfferEdited] = useState(false);
  
  // Team contract status state
  const [teamContractStatus, setTeamContractStatus] = useState(null);
  
  // Payment breakdown state
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [showPaymentBreakdownDialog, setShowPaymentBreakdownDialog] = useState(false);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  // Approval form state
  const [approvalData, setApprovalData] = useState({
    final_budget: 0,
    holding_margin: 0,
    team_budget: 0,
    payment_terms_days: 14,
    message: ""
  });
  const [isApprovalEdited, setIsApprovalEdited] = useState(false);
  
  const [rejectionReason, setRejectionReason] = useState("");

  // Auto-scroll to bottom of CHAT CONTAINER only (not whole page)
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [project?.negotiation_messages]);

  useEffect(() => {
    fetchProject();
  }, [projectId, token]);

  // Mark project-related notifications as read when page loads
  useEffect(() => {
    if (projectId && token) {
      fetch(`${API_URL}/api/notifications/mark-read-for-project/${projectId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
  }, [projectId, token]);

  // Polling for new messages every 5 seconds
  useEffect(() => {
    const pollInterval = setInterval(() => {
      fetchMessagesOnly();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [projectId, token]);

  useEffect(() => {
    if (project) {
      // If project is already approved, use values from DB
      if (project.final_budget && project.status !== "pending_holding_review") {
        // Use full breakdown to calculate correctly with platform fee
        calculateFullBreakdown(project.final_budget, project.holding_margin || 0);
        
        // Set approval data from project
        if (!isApprovalEdited) {
          setApprovalData(prev => ({
            ...prev,
            final_budget: project.final_budget,
            holding_margin: project.holding_margin || 0,
            team_budget: project.team_budget
          }));
        }
      } else if (project.proposed_budget) {
        // Project not approved yet - use proposed budget for preview
        const proposed = project.proposed_budget;
        const margin = Math.round(proposed * 0.10); // 10% default margin
        
        // Only update approval data if user hasn't edited it
        if (!isApprovalEdited) {
          setApprovalData(prev => ({
            ...prev,
            final_budget: proposed,
            holding_margin: margin,
            team_budget: 0 // Will be calculated by backend
          }));
        }
        
        // Calculate full breakdown (will also set team_budget correctly)
        calculateFullBreakdown(proposed, margin);
      }
      
      // Only update counter-offer if user hasn't edited it
      if (!isCounterOfferEdited && project.proposed_budget) {
        setCounterOfferData(prev => ({
          ...prev,
          proposed_budget: project.proposed_budget
        }));
      }
    }
  }, [project]);

  // Fetch only messages without full reload
  const fetchMessagesOnly = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/browse/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Only update messages if they changed
        if (JSON.stringify(data.negotiation_messages) !== JSON.stringify(project?.negotiation_messages)) {
          setProject(prev => prev ? { ...prev, negotiation_messages: data.negotiation_messages } : null);
        }
      }
    } catch (error) {
      // Silent fail for polling
    }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/projects-v3/browse/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Project not found");
      
      const data = await res.json();
      setProject(data);
      
      // Fetch team contract status if team is selected
      const hasTeam = data.team_leader_id || 
                      ["team_selected", "team_contracted", "in_progress", "completed"].includes(data.status) ||
                      data.applications?.some(app => app.status === "selected");
      if (hasTeam) {
        fetchTeamContractStatus();
      }
      
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Kunne ikke laste prosjekt");
      navigate("/holding/projects");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTeamContractStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/client/team-contract-status/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTeamContractStatus(data);
      }
    } catch (error) {
      console.error("Error fetching team contract status:", error);
    }
  };
  
  const fetchPaymentBreakdown = async () => {
    setLoadingBreakdown(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/holding/payment-breakdown/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPaymentBreakdown(data);
        setShowPaymentBreakdownDialog(true);
      } else {
        toast.error("Kunne ikke laste betalingsoversikt");
      }
    } catch (error) {
      console.error("Error fetching payment breakdown:", error);
      toast.error("Feil ved lasting av betalingsoversikt");
    } finally {
      setLoadingBreakdown(false);
    }
  };

  // Handle PDF view/download with authentication
  const handlePdfAction = async (url, filename, action) => {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      if (action === 'view') {
        window.open(blobUrl, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error('PDF action error:', error);
      toast.error('Kunne ikke laste PDF');
    }
  };

  // Fetch team messages for team chat
  const fetchTeamMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/chat/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTeamMessages(data.messages || []);
      }
    } catch (error) {
      console.error("Error fetching team messages:", error);
    }
  };

  // Fetch daily progress
  const fetchDailyProgress = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/${projectId}/daily-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDailyProgress(data.progress || []);
      }
    } catch (error) {
      console.error("Error fetching daily progress:", error);
    }
  };

  // Send team message
  const handleSendTeamMessage = async () => {
    if (!newTeamMessage.trim()) return;
    
    setSendingTeamMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/chat/project/${projectId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ message: newTeamMessage })
      });
      
      if (res.ok) {
        setNewTeamMessage("");
        fetchTeamMessages();
        toast.success("Melding sendt til team");
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      console.error("Error sending team message:", error);
      toast.error("Kunne ikke sende melding");
    } finally {
      setSendingTeamMessage(false);
    }
  };

  // Auto-scroll team chat
  const scrollTeamChatToBottom = () => {
    if (teamChatContainerRef.current) {
      teamChatContainerRef.current.scrollTop = teamChatContainerRef.current.scrollHeight;
    }
  };

  // Effect for team chat polling when on team chat tab
  useEffect(() => {
    if (activeTab === "team-chat" && project) {
      fetchTeamMessages();
      const interval = setInterval(fetchTeamMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, projectId]);

  // Effect for daily progress when on that tab
  useEffect(() => {
    if (activeTab === "progress" && project) {
      fetchDailyProgress();
    }
  }, [activeTab, projectId]);

  // Calculate fee breakdown for APPROVAL FORM (when setting budgets)
  // Uses gross_amount as client payment to preview all deductions
  const calculateFeesForApproval = async (grossAmount) => {
    try {
      const res = await fetch(`${API_URL}/api/holding/fees/calculate?gross_amount=${grossAmount}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFeeBreakdown(data);
      }
    } catch (error) {
      console.error("Error calculating fees:", error);
    }
  };
  
  // Calculate FULL fee breakdown from client payment and holding margin
  // This is the CORRECT calculation including platform fee
  const calculateFullBreakdown = async (clientPays, holdingMargin) => {
    try {
      const res = await fetch(
        `${API_URL}/api/holding/fees/full-breakdown?client_pays=${clientPays}&holding_margin=${holdingMargin}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        // Transform to match the expected feeBreakdown structure
        setFeeBreakdown({
          gross_amount: data.client_pays,
          platform_fee: data.platform_fee,
          platform_fee_percent: data.platform_fee_percent,
          holding_margin: data.holding_margin,
          team_budget_gross: data.team_budget,
          deductions: {
            aga: data.employer_costs.aga,
            aga_percent: data.employer_costs.aga_percent,
            feriepenger: data.employer_costs.feriepenger,
            feriepenger_percent: data.employer_costs.feriepenger_percent,
            andre_kostnader: data.employer_costs.andre_kostnader,
            andre_kostnader_percent: data.employer_costs.andre_kostnader_percent,
            total_deductions: data.employer_costs.total
          },
          net_to_team: data.net_to_team,
          profit: data.profit
        });
        
        // Also update approval data with correct team_budget
        setApprovalData(prev => ({
          ...prev,
          team_budget: data.team_budget
        }));
      }
    } catch (error) {
      console.error("Error calculating full breakdown:", error);
    }
  };

  const handleBudgetChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    
    setIsApprovalEdited(true); // Mark as edited
    
    let newFinalBudget = approvalData.final_budget;
    let newHoldingMargin = approvalData.holding_margin;
    
    setApprovalData(prev => {
      const updated = { ...prev, [field]: numValue };
      
      // Update local variables for calculation
      if (field === "final_budget") {
        newFinalBudget = numValue;
        // Keep holding margin, team_budget will be calculated by backend
      } else if (field === "holding_margin") {
        newHoldingMargin = numValue;
        // team_budget will be calculated by backend
      }
      
      return updated;
    });
    
    // Recalculate using full breakdown (includes platform fee correctly)
    if (field === "final_budget") {
      calculateFullBreakdown(numValue, newHoldingMargin);
    } else if (field === "holding_margin") {
      calculateFullBreakdown(newFinalBudget, numValue);
    }
  };

  const handleApprove = async () => {
    if (approvalData.team_budget <= 0) {
      toast.error("Team budget må være større enn 0");
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/holding/negotiate/${projectId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: "approve",
          final_budget: approvalData.final_budget,
          holding_margin: approvalData.holding_margin,
          team_budget: approvalData.team_budget,
          payment_terms_days: approvalData.payment_terms_days,
          message: approvalData.message
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Could not approve project");
      }
      
      toast.success("Prosjekt godkjent!");
      setShowApproveDialog(false);
      setIsApprovalEdited(false); // Reset edit flag
      fetchProject();
      
    } catch (error) {
      console.error("Error approving:", error);
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Vennligst oppgi en grunn for avslaget");
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/holding/negotiate/${projectId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: "reject",
          message: rejectionReason
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Could not reject project");
      }
      
      toast.success("Prosjekt avslått");
      navigate("/holding/projects");
      
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendCounterOffer = async () => {
    if (!counterOfferData.proposed_budget || counterOfferData.proposed_budget <= 0) {
      toast.error("Vennligst oppgi et gyldig budsjett");
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/holding/counter-offer-to-client/${projectId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          proposed_budget: counterOfferData.proposed_budget,
          message: counterOfferData.message
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Could not send counter-offer");
      }
      
      toast.success("Mot-tilbud sendt til klient!");
      setShowCounterOfferDialog(false);
      setIsCounterOfferEdited(false); // Reset edit flag
      fetchProject();
      
    } catch (error) {
      console.error("Error sending counter-offer:", error);
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/${projectId}/negotiation-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: chatMessage.trim()
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Could not send message");
      }
      
      toast.success("Melding sendt!");
      setChatMessage("");
      fetchProject(); // Refresh to get new messages
      
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_holding_review: { label: "Venter Godkjenning", color: "bg-yellow-500/20 text-yellow-400" },
      approved_pending_client_sign: { label: "Venter Klient Signatur", color: "bg-blue-500/20 text-blue-400" },
      live: { label: "Live", color: "bg-green-500/20 text-green-400" },
      has_applications: { label: "Har Søknader", color: "bg-purple-500/20 text-purple-400" },
      team_selected: { label: "Team Valgt", color: "bg-cyan-500/20 text-cyan-400" },
      rejected: { label: "Avslått", color: "bg-red-500/20 text-red-400" }
    };
    
    const config = statusConfig[status] || { label: status, color: "bg-gray-500/20 text-gray-400" };
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
          <h2 className="text-white text-xl">Prosjekt ikke funnet</h2>
        </div>
      </DashboardLayout>
    );
  }

  const isPending = project.status === "pending_holding_review";

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="holding-project-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/holding/projects")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{project.title}</h1>
              {getStatusBadge(project.status)}
            </div>
            <p className="text-gray-400 mt-1">
              Opprettet {formatDate(project.created_at)}
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={activeTab === "details" ? "default" : "outline"}
            onClick={() => setActiveTab("details")}
            className={activeTab === "details" 
              ? "bg-[#23F7DD] text-black" 
              : "border-[#2a2a4a] text-gray-400 hover:text-white"}
          >
            <FileText className="w-4 h-4 mr-2" />
            Detaljer
          </Button>
          <Button
            variant={activeTab === "contract" ? "default" : "outline"}
            onClick={() => setActiveTab("contract")}
            className={activeTab === "contract" 
              ? "bg-[#23F7DD] text-black" 
              : "border-[#2a2a4a] text-gray-400 hover:text-white"}
          >
            <FileText className="w-4 h-4 mr-2" />
            Kontrakt
          </Button>
          <Button
            variant={activeTab === "applications" ? "default" : "outline"}
            onClick={() => setActiveTab("applications")}
            className={activeTab === "applications" 
              ? "bg-[#23F7DD] text-black" 
              : "border-[#2a2a4a] text-gray-400 hover:text-white"}
          >
            <Users className="w-4 h-4 mr-2" />
            Søknader ({project.applications?.length || 0})
          </Button>
          <Button
            variant={activeTab === "progress" ? "default" : "outline"}
            onClick={() => setActiveTab("progress")}
            className={activeTab === "progress" 
              ? "bg-[#23F7DD] text-black" 
              : "border-[#2a2a4a] text-gray-400 hover:text-white"}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Daglig Progresjon
          </Button>
          <Button
            variant={activeTab === "team-chat" ? "default" : "outline"}
            onClick={() => setActiveTab("team-chat")}
            className={activeTab === "team-chat" 
              ? "bg-purple-600 text-white" 
              : "border-[#2a2a4a] text-gray-400 hover:text-white"}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Team Chat
          </Button>
        </div>

        {/* Details Tab Content */}
        {activeTab === "details" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Details */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">Prosjektdetaljer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Klient</p>
                      <p className="text-white">{project.client_company || project.client_name}</p>
                      {project.client_org_number && (
                        <p className="text-gray-500 text-xs">Org: {project.client_org_number}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Lokasjon</p>
                      <p className="text-white">{project.location_city}</p>
                      <p className="text-gray-500 text-xs">{project.location_address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Bemanning</p>
                      <p className="text-white">{project.crew_size} personer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Varighet</p>
                      <p className="text-white">{project.estimated_duration_days} dager</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Startdato</p>
                      <p className="text-white">{formatDate(project.start_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Type</p>
                      <p className="text-white">
                        {project.project_type === "workforce" ? "Workforce" : "Stillastjenester"}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-[#2a2a4a]">
                  <p className="text-gray-400 text-sm mb-2">Beskrivelse</p>
                  <p className="text-white whitespace-pre-wrap">{project.description}</p>
                </div>
                
                {project.special_requirements && (
                  <div className="pt-4 border-t border-[#2a2a4a]">
                    <p className="text-gray-400 text-sm mb-2">Spesielle krav</p>
                    <p className="text-white whitespace-pre-wrap">{project.special_requirements}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applications Section (if any) */}
            {project.applications?.length > 0 && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-400" />
                    {t('projectDetail.applications')} ({project.applications.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.applications.map((app) => {
                    const isSelected = app.status === "selected" || 
                      project.selected_team?.application_id === app.application_id;
                    
                    return (
                      <div 
                        key={app.application_id} 
                        className={`p-4 rounded-lg border transition-all ${
                          isSelected 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : app.status === "rejected"
                            ? 'bg-red-500/5 border-red-500/20'
                            : 'bg-[#0d0d1a] border-[#2a2a4a] hover:border-purple-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Team Leader Info */}
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{app.team_leader_name}</p>
                                <p className="text-gray-500 text-sm">
                                  {t('roles.teamLeader')} • {app.team_members?.length || 0} {t('projectDetail.teamMembers')}
                                </p>
                              </div>
                              {app.has_warning && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {t('common.warning')}
                                </Badge>
                              )}
                            </div>
                            
                            {app.message && (
                              <div className="mb-3 p-3 bg-[#1a1a2e] rounded-lg">
                                <p className="text-gray-400 text-sm flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
                                  "{app.message}"
                                </p>
                              </div>
                            )}
                            
                            {/* Team Members Section */}
                            {app.team_members?.length > 0 && (
                              <div className="mt-4">
                                <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  {t('projects.workers')} ({app.team_members.length})
                                </p>
                                <div className="grid gap-2">
                                  {app.team_members.map((member, idx) => (
                                    <div 
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-[#1a1a2e] rounded-lg border border-[#2a2a4a]"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                          <User className="w-4 h-4 text-blue-400" />
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <p className="text-white text-sm font-medium">{member.name}</p>
                                            {member.worker_code && (
                                              <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                                                {member.worker_code}
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-gray-500 text-xs">{member.role || 'Arbeider'}</p>
                                        </div>
                                      </div>
                                      {/* Rating Stars */}
                                      <div className="flex items-center gap-1">
                                        {member.rating > 0 ? (
                                          <div className="flex items-center gap-1 text-yellow-400">
                                            <Star className="w-4 h-4 fill-yellow-400" />
                                            <span className="text-sm">{member.rating.toFixed(1)}</span>
                                          </div>
                                        ) : (
                                          <span className="text-gray-500 text-xs">{t('common.noRating')}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {isSelected ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchPaymentBreakdown();
                                  }}
                                  disabled={loadingBreakdown}
                                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                                >
                                  <Wallet className="w-4 h-4 mr-1" />
                                  {loadingBreakdown ? "..." : "Utbetalinger"}
                                </Button>
                                <Badge className="bg-green-500/20 text-green-400 border-0">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {t('status.selected')}
                                </Badge>
                              </>
                            ) : app.status === "rejected" ? (
                              <Badge className="bg-red-500/20 text-red-400 border-0">
                                {t('status.rejected')}
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-0">
                                {t('status.pending')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Project Documents Section - support both old and new field names */}
            {(project.project_documents?.length > 0 || project.project_images?.length > 0 || 
              project.photos?.length > 0 || project.documents?.length > 0) && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Prosjektdokumenter
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Project Images - check both field names */}
                  {((project.project_images?.length > 0) || (project.photos?.length > 0)) && (
                    <div>
                      <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Bilder ({(project.project_images?.length || 0) + (project.photos?.length || 0)})
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[...(project.project_images || []), ...(project.photos || [])].map((img, idx) => {
                          const imgUrl = typeof img === 'string' ? img : img.url;
                          const imgName = typeof img === 'string' ? `Bilde ${idx + 1}` : (img.name || `Bilde ${idx + 1}`);
                          return (
                            <a 
                              key={idx} 
                              href={imgUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="relative group aspect-video rounded-lg overflow-hidden border border-[#2a2a4a] hover:border-[#23F7DD] transition-colors"
                            >
                              <img 
                                src={imgUrl} 
                                alt={imgName}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Project Documents (PDFs, IFCs) - check both field names */}
                  {((project.project_documents?.length > 0) || (project.documents?.length > 0)) && (
                    <div className={((project.project_images?.length > 0) || (project.photos?.length > 0)) ? "pt-4 border-t border-[#2a2a4a]" : ""}>
                      <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Dokumenter ({(project.project_documents?.length || 0) + (project.documents?.length || 0)})
                      </p>
                      <div className="space-y-2">
                        {[...(project.project_documents || []), ...(project.documents || [])].map((doc, idx) => {
                          const docUrl = typeof doc === 'string' ? doc : doc.url;
                          const docName = typeof doc === 'string' ? `Dokument ${idx + 1}` : (doc.name || `Dokument ${idx + 1}`);
                          const docType = typeof doc === 'string' ? 'PDF' : (doc.type?.toUpperCase() || 'PDF');
                          return (
                            <a 
                              key={idx}
                              href={docUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 bg-[#0f0f1a] rounded-lg hover:bg-[#2a2a4a] transition-colors group"
                            >
                              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white truncate">{docName}</p>
                                <p className="text-gray-500 text-xs">
                                  {docType} • Klikk for å åpne
                                </p>
                              </div>
                              <Download className="w-5 h-5 text-gray-400 group-hover:text-[#23F7DD] transition-colors" />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SJA (Safe Job Analysis) Section */}
            {project.sja_form && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-orange-400" />
                    {t('sja.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* General SJA Info - support both old and new field names */}
                  {(project.sja_form.work_description || project.sja_form.job_description) && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">{t('sja.workDescription')}</p>
                      <p className="text-white">{project.sja_form.work_description || project.sja_form.job_description}</p>
                    </div>
                  )}

                  {/* Hazards - support both string array and object array formats */}
                  {project.sja_form.hazards?.length > 0 && (
                    <div>
                      <p className="text-gray-400 text-sm mb-2">{t('sja.hazardsTitle')} ({project.sja_form.hazards.length})</p>
                      <div className="space-y-3">
                        {project.sja_form.hazards.map((hazard, idx) => {
                          // Support different hazard formats
                          const hazardId = typeof hazard === 'string' 
                            ? hazard 
                            : hazard.name || hazard.hazard || hazard.id || 'other';
                          
                          // Map hazard IDs to translation keys (snake_case to camelCase)
                          const hazardKeyMap = {
                            'fall_height': 'fallHeight',
                            'fallheight': 'fallHeight',
                            'fall from height': 'fallHeight',
                            'falling_objects': 'fallingObjects',
                            'fallingobjects': 'fallingObjects',
                            'electrical': 'electrical',
                            'weather': 'weather',
                            'traffic': 'traffic',
                            'heavy_lifting': 'heavyLifting',
                            'heavylifting': 'heavyLifting',
                            'confined_space': 'confinedSpace',
                            'confinedspace': 'confinedSpace',
                            'noise': 'noise',
                            'dust': 'dust',
                            'chemicals': 'chemicals',
                            'other': 'other'
                          };
                          
                          const hazardLower = hazardId.toLowerCase().replace(/\s+/g, '');
                          const translationKey = hazardKeyMap[hazardLower] || hazardKeyMap[hazardId.toLowerCase()] || hazardId;
                          const translatedHazard = t(`sja.hazards.${translationKey}`, { defaultValue: '' }) || 
                                                   hazardId.replace(/_/g, ' ');
                          
                          const measures = hazard.preventive_measures || hazard.measures || '';
                          const responsible = hazard.responsible_person || hazard.responsible || '';
                          const probability = hazard.probability || '';
                          const consequence = hazard.consequence || '';
                          
                          return (
                            <div key={idx} className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-red-400 font-medium capitalize">
                                    {translatedHazard}
                                  </p>
                                  {(probability || consequence) && (
                                    <div className="flex gap-4 mt-1 text-xs text-gray-400">
                                      {probability && <span>{t('sja.probability')}: {t(`sja.${probability}`, { defaultValue: probability })}</span>}
                                      {consequence && <span>{t('sja.consequence')}: {t(`sja.${consequence}`, { defaultValue: consequence })}</span>}
                                    </div>
                                  )}
                                  {measures && (
                                    <div className="mt-2 pl-4 border-l-2 border-green-500/30">
                                      <p className="text-gray-400 text-xs uppercase mb-1">{t('sja.preventiveMeasures')}</p>
                                      <p className="text-green-400 text-sm">{measures}</p>
                                    </div>
                                  )}
                                  {responsible && (
                                    <div className="mt-2 flex items-center gap-2 text-sm">
                                      <User className="w-3 h-3 text-gray-400" />
                                      <span className="text-gray-400">{t('sja.responsiblePerson')}:</span>
                                      <span className="text-white">{responsible}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* PPE Requirements - support both old and new field names */}
                  {(project.sja_form.ppe_requirements?.length > 0 || project.sja_form.ppe_required?.length > 0) && (
                    <div className="pt-4 border-t border-[#2a2a4a]">
                      <p className="text-gray-400 text-sm mb-2">{t('sja.ppeRequired')}</p>
                      <div className="flex flex-wrap gap-2">
                        {(project.sja_form.ppe_requirements || project.sja_form.ppe_required || []).map((ppe, idx) => {
                          // Translate PPE item
                          const ppeId = typeof ppe === 'string' ? ppe : ppe.id || ppe;
                          const ppeKey = ppeId.replace(/[_\s]+/g, '').toLowerCase();
                          // Map common PPE IDs to translation keys
                          const ppeKeyMap = {
                            'helmet': 'helmet',
                            'harness': 'harness',
                            'gloves': 'gloves',
                            'safetyshoes': 'safetyShoes',
                            'safety_shoes': 'safetyShoes',
                            'hivis': 'hiVis',
                            'hi_vis': 'hiVis',
                            'safetyglasses': 'safetyGlasses',
                            'safety_glasses': 'safetyGlasses',
                            'earprotection': 'earProtection',
                            'ear_protection': 'earProtection',
                            'dustmask': 'dustMask',
                            'dust_mask': 'dustMask'
                          };
                          const translationKey = ppeKeyMap[ppeKey] || ppeKey;
                          const translatedPpe = t(`sja.ppe.${translationKey}`, { defaultValue: '' }) || ppeId.replace(/_/g, ' ');
                          
                          return (
                            <Badge key={idx} className="bg-blue-500/20 text-blue-400 border-0 capitalize">
                              {translatedPpe}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Emergency Procedures / Additional Measures */}
                  {(project.sja_form.emergency_procedures || project.sja_form.additional_measures) && (
                    <div className="pt-4 border-t border-[#2a2a4a]">
                      <p className="text-gray-400 text-sm mb-1">{t('sja.additionalMeasures')}</p>
                      <p className="text-white whitespace-pre-wrap">{project.sja_form.emergency_procedures || project.sja_form.additional_measures}</p>
                    </div>
                  )}

                  {/* Emergency Contact Info */}
                  {(project.sja_form.emergency_contact || project.sja_form.nearest_hospital || project.sja_form.first_aid_location) && (
                    <div className="pt-4 border-t border-[#2a2a4a]">
                      <p className="text-gray-400 text-sm mb-2">{t('sja.emergencyInfo')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        {project.sja_form.emergency_contact && (
                          <div>
                            <p className="text-gray-500 text-xs">{t('sja.emergencyContact')}</p>
                            <p className="text-white">{project.sja_form.emergency_contact}</p>
                          </div>
                        )}
                        {project.sja_form.nearest_hospital && (
                          <div>
                            <p className="text-gray-500 text-xs">{t('sja.nearestHospital')}</p>
                            <p className="text-white">{project.sja_form.nearest_hospital}</p>
                          </div>
                        )}
                        {project.sja_form.first_aid_location && (
                          <div>
                            <p className="text-gray-500 text-xs">{t('sja.firstAidLocation')}</p>
                            <p className="text-white">{project.sja_form.first_aid_location}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Work Permits */}
                  {project.sja_form.work_permits?.length > 0 && (
                    <div className="pt-4 border-t border-[#2a2a4a]">
                      <p className="text-gray-400 text-sm mb-2">Arbeidstillatelser</p>
                      <div className="flex flex-wrap gap-2">
                        {project.sja_form.work_permits.map((permit, idx) => (
                          <Badge key={idx} className="bg-purple-500/20 text-purple-400 border-0">
                            {permit}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Responsible Person */}
                  {project.sja_form.responsible_person && (
                    <div className="pt-4 border-t border-[#2a2a4a]">
                      <p className="text-gray-400 text-sm mb-1">SJA Ansvarlig</p>
                      <p className="text-white">{project.sja_form.responsible_person}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Budget Card */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                  Budsjett
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Foreslått budsjett</span>
                  <span className="text-white font-bold text-xl">
                    {formatCurrency(project.proposed_budget)}
                  </span>
                </div>
                
                {project.final_budget && (
                  <>
                    <div className="border-t border-[#2a2a4a] pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Godkjent budsjett</span>
                        <span className="text-[#23F7DD] font-bold text-xl">
                          {formatCurrency(project.final_budget)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Holding margin</span>
                      <span className="text-white">
                        {formatCurrency(project.holding_margin)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Team budsjett</span>
                      <span className="text-white">
                        {formatCurrency(project.team_budget)}
                      </span>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Betalingsfrist</span>
                  <span className="text-white">{project.payment_terms_days || "-"} dager</span>
                </div>
                
                {/* Download Contract Button - show only if signed */}
                {project.status !== "pending_holding_review" && project.client_signed && (
                  <div className="border-t border-[#2a2a4a] pt-4 mt-4">
                    <Button 
                      className="w-full bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                      onClick={() => window.open(`${API_URL}/api/contracts-pdf/client-holding/${projectId}`, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Last ned kontrakt (PDF)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions Card */}
            {isPending && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white">Handlinger</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => setShowApproveDialog(true)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Godkjenn Prosjekt
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    onClick={() => setShowCounterOfferDialog(true)}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Send Mot-tilbud
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => setShowRejectDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Avslå Prosjekt
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Fee Calculator - Full breakdown for Holding Admin */}
            {feeBreakdown && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#23F7DD]" />
                    Gebyrberegning (Intern)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Klient betaler</span>
                    <span className="text-white font-medium">{formatCurrency(feeBreakdown.gross_amount)}</span>
                  </div>
                  
                  {/* Top-level deductions (from gross) */}
                  <div className="bg-red-500/10 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-red-400">
                      <span>Platform fee ({feeBreakdown.platform_fee_percent}%)</span>
                      <span>-{formatCurrency(feeBreakdown.platform_fee)}</span>
                    </div>
                    {feeBreakdown.holding_margin > 0 && (
                      <div className="flex justify-between text-yellow-400">
                        <span>Holding margin ({feeBreakdown.holding_margin_percent}%)</span>
                        <span>-{formatCurrency(feeBreakdown.holding_margin)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-[#2a2a4a] pt-3 flex justify-between">
                    <span className="text-gray-400">Team budsjett (brutto)</span>
                    <span className="text-white">{formatCurrency(feeBreakdown.team_budget_gross)}</span>
                  </div>
                  
                  {/* Employer costs (from team budget) */}
                  <div className="bg-orange-500/10 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-orange-400">
                      <span>AGA ({feeBreakdown.deductions?.aga_percent}%)</span>
                      <span>-{formatCurrency(feeBreakdown.deductions?.aga)}</span>
                    </div>
                    <div className="flex justify-between text-orange-400">
                      <span>Feriepenger ({feeBreakdown.deductions?.feriepenger_percent}%)</span>
                      <span>-{formatCurrency(feeBreakdown.deductions?.feriepenger)}</span>
                    </div>
                    <div className="flex justify-between text-orange-400">
                      <span>Andre kostnader ({feeBreakdown.deductions?.andre_kostnader_percent}%)</span>
                      <span>-{formatCurrency(feeBreakdown.deductions?.andre_kostnader)}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-[#2a2a4a] pt-3 flex justify-between font-medium">
                    <span className="text-gray-400">Netto til team (brutto lønn)</span>
                    <span className="text-[#23F7DD] text-lg">{formatCurrency(feeBreakdown.net_to_team)}</span>
                  </div>
                  
                  {/* Profit summary */}
                  <div className="bg-green-500/10 rounded-lg p-3 space-y-2 mt-2">
                    <p className="text-green-400 text-xs font-medium uppercase">Fortjeneste</p>
                    <div className="flex justify-between text-green-400">
                      <span>Platform</span>
                      <span>{formatCurrency(feeBreakdown.platform_fee)}</span>
                    </div>
                    {feeBreakdown.holding_margin > 0 && (
                      <div className="flex justify-between text-yellow-400">
                        <span>Holding</span>
                        <span>{formatCurrency(feeBreakdown.holding_margin)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 pt-2">
                    <div>Per person (brutto): {formatCurrency(feeBreakdown.net_to_team / (project.crew_size || 1))}</div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Contract Status - for Holding Admin */}
            {teamContractStatus && teamContractStatus.has_team && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Team Kontrakt Status
                    <Badge className={`ml-2 ${teamContractStatus.all_members_signed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {teamContractStatus.signed_count}/{teamContractStatus.total_members} signert
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {teamContractStatus.members.map((member, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          member.has_signed 
                            ? 'bg-green-500/5 border-green-500/30' 
                            : 'bg-[#0f0f1a] border-[#2a2a4a]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              member.role === 'Basleder' || member.role === 'Team Leader'
                                ? 'bg-purple-500/20 text-purple-400'
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              <User className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{member.name}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <Badge variant="outline" className="text-xs border-[#2a2a4a] text-gray-400 py-0">
                                  {member.role}
                                </Badge>
                                {member.worker_code && (
                                  <span className="text-gray-500">{member.worker_code}</span>
                                )}
                                {member.rating > 0 && (
                                  <span className="flex items-center gap-1 text-yellow-400">
                                    <Star className="w-3 h-3 fill-yellow-400" />
                                    {member.rating.toFixed(1)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {member.has_signed ? (
                              <div className="flex items-center gap-2 text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <div>
                                  <p className="font-medium text-xs">Signert</p>
                                  {member.signed_at && (
                                    <p className="text-xs text-gray-500">
                                      {new Date(member.signed_at).toLocaleDateString('no-NO')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-yellow-400">
                                <Clock className="w-4 h-4" />
                                <span className="font-medium text-xs">Venter</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4 pt-4 border-t border-[#2a2a4a]">
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-400">Signerings-fremgang</span>
                      <span className="text-white font-medium">
                        {Math.round((teamContractStatus.signed_count / teamContractStatus.total_members) * 100)}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-[#0f0f1a] rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          teamContractStatus.all_members_signed ? 'bg-green-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${(teamContractStatus.signed_count / teamContractStatus.total_members) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Download Team Contract */}
                  {project.team_contract_signed && (
                    <div className="mt-4 pt-4 border-t border-[#2a2a4a]">
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => window.open(`${API_URL}/api/contracts-pdf/team/${projectId}`, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Last ned Team Kontrakt (PDF)
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Negotiation Chat */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]" data-testid="negotiation-chat">
              <CardHeader className="pb-3">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <MessageSquare className="w-5 h-5 text-[#23F7DD]" />
                  Kommunikasjon med klient
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Messages */}
                <div 
                  ref={chatContainerRef}
                  className="max-h-[300px] overflow-y-auto space-y-3 p-2 bg-[#0f0f1a] rounded-lg min-h-[150px]"
                >
                  {project.negotiation_messages?.length > 0 ? (
                    <>
                      {project.negotiation_messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.from === "holding" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-lg p-3 ${
                              msg.from === "holding"
                                ? "bg-[#23F7DD]/20 text-white"
                                : "bg-white/10 text-white"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <User className="w-3 h-3" />
                              <span className="text-xs font-medium">
                                {msg.from === "holding" ? "Holding" : (msg.sender_name || "Klient")}
                              </span>
                            </div>
                            <p className="text-sm">{msg.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(msg.timestamp).toLocaleString("no-NO", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                            {msg.action && (
                              <Badge className={`mt-2 ${
                                msg.action === "approve" ? "bg-green-500/20 text-green-400" :
                                msg.action === "reject" ? "bg-red-500/20 text-red-400" :
                                "bg-yellow-500/20 text-yellow-400"
                              }`}>
                                {msg.action === "approve" ? "Godkjent" : 
                                 msg.action === "reject" ? "Avslått" : "Mottilbud"}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-[100px] text-gray-500 text-sm">
                      <div className="text-center">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>Ingen meldinger ennå</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Message Input */}
                <div className="flex gap-2">
                  <Input
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Skriv en melding..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !chatMessage.trim()}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}

        {/* Contract Tab Content */}
        {activeTab === "contract" && (
          <div className="space-y-6">
            {/* Sub-tabs for contract types */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={contractSubTab === "holding" ? "default" : "outline"}
                onClick={() => setContractSubTab("holding")}
                className={contractSubTab === "holding" 
                  ? "bg-blue-600 text-white" 
                  : "border-[#2a2a4a] text-gray-400 hover:text-white"}
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('projectDetail.contractWithHolding') || 'Kontrakt med Klient'}
              </Button>
              {teamContractStatus?.has_team && (
                <Button
                  variant={contractSubTab === "team" ? "default" : "outline"}
                  onClick={() => setContractSubTab("team")}
                  className={contractSubTab === "team" 
                    ? "bg-purple-600 text-white" 
                    : "border-[#2a2a4a] text-gray-400 hover:text-white"}
                >
                  <Users className="w-4 h-4 mr-2" />
                  {t('projectDetail.contractWithTeam') || 'Kontrakt med Team'}
                  <Badge className={`ml-2 ${teamContractStatus.all_members_signed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {teamContractStatus.signed_count}/{teamContractStatus.total_members}
                  </Badge>
                </Button>
              )}
            </div>

            {/* Client-Holding Contract */}
            {contractSubTab === "holding" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      {t('projectDetail.platformContract') || 'Klient-Holding Kontrakt'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {project.client_signed ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-green-400 mb-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-semibold">{t('projectDetail.contractSigned') || 'Kontrakt Signert'}</span>
                          </div>
                          <p className="text-gray-400 text-sm">
                            {t('projectDetail.signedOn') || 'Signert'} {project.client_signed_at ? formatDate(project.client_signed_at) : ''}
                          </p>
                        </div>
                        
                        {/* Contract Details */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-[#0f0f1a] rounded-lg">
                            <p className="text-gray-400 text-xs mb-1">{t('projectDetail.signedBy') || 'Signert av'}</p>
                            <p className="text-white font-medium">{project.client_signer_name || project.client_name || 'Klient'}</p>
                          </div>
                          <div className="p-3 bg-[#0f0f1a] rounded-lg">
                            <p className="text-gray-400 text-xs mb-1">{t('projectDetail.signedDate') || 'Dato'}</p>
                            <p className="text-white font-medium">{project.client_signed_at ? formatDate(project.client_signed_at) : '-'}</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handlePdfAction(
                              `${API_URL}/api/contracts-pdf/client-holding/${projectId}`,
                              `contract-holding-${projectId}.pdf`,
                              'view'
                            )}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            {t('projectDetail.viewContract') || 'Se Kontrakt'}
                          </Button>
                          <Button 
                            className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                            onClick={() => handlePdfAction(
                              `${API_URL}/api/contracts-pdf/client-holding/${projectId}`,
                              `contract-holding-${projectId}.pdf`,
                              'download'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('projectDetail.downloadContract') || 'Last ned'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Clock className="w-10 h-10 mx-auto text-yellow-400 mb-3" />
                        <p className="text-gray-400">{t('projectDetail.waitingForClientSignature') || 'Venter på klient signatur'}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Financial Overview - same as Client */}
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-green-400" />
                      {t('projectDetail.financialOverview') || 'Finansiell Oversikt'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-[#0f0f1a] rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{t('projectDetail.totalBudget') || 'Total Budsjett'}</span>
                        <span className="text-white font-bold text-lg">{formatCurrency(project.final_budget || project.proposed_budget)}</span>
                      </div>
                    </div>
                    
                    {feeBreakdown && (
                      <>
                        <div className="flex justify-between p-2 border-b border-[#2a2a4a]">
                          <span className="text-gray-400 text-sm">Platform fee (2%)</span>
                          <span className="text-red-400 text-sm">-{formatCurrency(feeBreakdown.platform_fee)}</span>
                        </div>
                        <div className="flex justify-between p-2 border-b border-[#2a2a4a]">
                          <span className="text-gray-400 text-sm">Holding margin</span>
                          <span className="text-yellow-400 text-sm">-{formatCurrency(feeBreakdown.holding_margin)}</span>
                        </div>
                        <div className="flex justify-between p-2 border-b border-[#2a2a4a]">
                          <span className="text-gray-400 text-sm">Team budsjett</span>
                          <span className="text-white text-sm">{formatCurrency(feeBreakdown.team_budget_gross)}</span>
                        </div>
                        <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                          <div className="flex justify-between items-center">
                            <span className="text-cyan-400 font-semibold">Netto til team</span>
                            <span className="text-cyan-400 font-bold">{formatCurrency(feeBreakdown.net_to_team)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Team Contract */}
            {contractSubTab === "team" && teamContractStatus?.has_team && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-400" />
                      {t('projectDetail.teamContract') || 'Team Kontrakt'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(project.team_contract_signed || teamContractStatus.all_members_signed) ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-green-400">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-semibold">{t('projectDetail.teamContractSigned') || 'Alle har signert'}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => handlePdfAction(
                              `${API_URL}/api/contracts-pdf/team/${projectId}`,
                              `team-contract-${projectId}.pdf`,
                              'view'
                            )}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            {t('projectDetail.viewContract') || 'Se Kontrakt'}
                          </Button>
                          <Button 
                            className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                            onClick={() => handlePdfAction(
                              `${API_URL}/api/contracts-pdf/team/${projectId}`,
                              `team-contract-${projectId}.pdf`,
                              'download'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('projectDetail.downloadContract') || 'Last ned'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <div className="flex items-center gap-2 text-yellow-400">
                          <Clock className="w-5 h-5" />
                          <span className="font-semibold">{t('projectDetail.waitingForTeamSignature') || 'Venter på signaturer'}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-400" />
                      {t('projectDetail.signatureStatus') || 'Signaturstatus'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {teamContractStatus.members.map((member, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${member.has_signed ? 'bg-green-500/5 border-green-500/30' : 'bg-[#0f0f1a] border-[#2a2a4a]'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${member.role === 'Basleder' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                <User className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">{member.name}</p>
                                <p className="text-gray-500 text-xs">{member.role} {member.worker_code && `• ${member.worker_code}`}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {member.has_signed ? (
                                <>
                                  <span className="text-green-400 text-xs">{t('common.signed') || 'Signert'}</span>
                                  <CheckCircle className="w-5 h-5 text-green-400" />
                                </>
                              ) : (
                                <>
                                  <span className="text-yellow-400 text-xs">{t('common.pending') || 'Venter'}</span>
                                  <Clock className="w-5 h-5 text-yellow-400" />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#2a2a4a]">
                      <div className="w-full h-2 bg-[#0f0f1a] rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${teamContractStatus.all_members_signed ? 'bg-green-500' : 'bg-purple-500'}`}
                          style={{ width: `${(teamContractStatus.signed_count / teamContractStatus.total_members) * 100}%` }}
                        />
                      </div>
                      <p className="text-center text-gray-400 text-sm mt-2">
                        {teamContractStatus.signed_count}/{teamContractStatus.total_members} {t('common.signed') || 'signert'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Applications Tab Content */}
        {activeTab === "applications" && (
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#23F7DD]" />
                {t('projectDetail.applications')} ({project.applications?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {project.applications?.length > 0 ? (
                <div className="space-y-4">
                  {project.applications.map((app, idx) => {
                    const isSelected = app.status === "selected" || 
                      project.selected_team?.application_id === app.application_id;
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border transition-all ${
                          isSelected 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : app.status === "rejected"
                            ? 'bg-red-500/5 border-red-500/20'
                            : 'bg-[#0d0d1a] border-[#2a2a4a] hover:border-purple-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Team Leader Info */}
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-medium">{app.team_leader_name}</p>
                                  {app.team_leader_code && (
                                    <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">
                                      {app.team_leader_code}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-500 text-sm">
                                  {t('roles.teamLeader')} • {app.team_members?.length || 0} {t('projectDetail.teamMembers')}
                                </p>
                              </div>
                              {app.has_warning && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {t('common.warning')}
                                </Badge>
                              )}
                            </div>
                            
                            {app.message && (
                              <div className="mb-3 p-3 bg-[#1a1a2e] rounded-lg">
                                <p className="text-gray-400 text-sm flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
                                  "{app.message}"
                                </p>
                              </div>
                            )}
                            
                            {/* Team Members Section */}
                            {app.team_members?.length > 0 && (
                              <div className="mt-4">
                                <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  {t('projects.workers')} ({app.team_members.length})
                                </p>
                                <div className="grid gap-2">
                                  {app.team_members.map((member, midx) => (
                                    <div 
                                      key={midx}
                                      className="flex items-center justify-between p-3 bg-[#1a1a2e] rounded-lg border border-[#2a2a4a]"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                          <User className="w-4 h-4 text-blue-400" />
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <p className="text-white text-sm font-medium">{member.name}</p>
                                            {member.worker_code && (
                                              <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                                                {member.worker_code}
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-gray-500 text-xs">{member.role || 'Arbeider'}</p>
                                        </div>
                                      </div>
                                      {/* Rating Stars */}
                                      <div className="flex items-center gap-1">
                                        {member.rating > 0 ? (
                                          <div className="flex items-center gap-1 text-yellow-400">
                                            <Star className="w-4 h-4 fill-yellow-400" />
                                            <span className="text-sm">{member.rating.toFixed(1)}</span>
                                          </div>
                                        ) : (
                                          <span className="text-gray-500 text-xs">{t('common.noRating')}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {isSelected ? (
                              <Badge className="bg-green-500/20 text-green-400 border-0">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t('status.selected')}
                              </Badge>
                            ) : app.status === "rejected" ? (
                              <Badge className="bg-red-500/20 text-red-400 border-0">
                                {t('status.rejected')}
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                                {t('status.pending')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-400">{t('applications.noApplications') || 'Ingen søknader ennå'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Daily Progress Tab Content */}
        {activeTab === "progress" && (
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
                Daglig Progresjon
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyProgress.length > 0 ? (
                <div className="space-y-4">
                  {dailyProgress.map((entry, idx) => (
                    <div key={idx} className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-blue-500/20 text-blue-400">Dag {entry.day_number || idx + 1}</Badge>
                        <span className="text-gray-400 text-sm">{entry.date ? new Date(entry.date).toLocaleDateString('no-NO') : ''}</span>
                      </div>
                      <p className="text-white mb-3">{entry.description || entry.message}</p>
                      {entry.photos?.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {entry.photos.map((photo, pidx) => (
                            <img key={pidx} src={photo} alt={`Progress ${pidx + 1}`} className="w-full h-24 object-cover rounded-lg border border-[#2a2a4a] cursor-pointer hover:opacity-80" onClick={() => window.open(photo, '_blank')} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-400">Ingen fremgangsrapporter ennå</p>
                  <p className="text-gray-500 text-sm">Oppdateringer kommer når arbeidet starter</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Team Chat Tab Content */}
        {activeTab === "team-chat" && (
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-400" />
                Chat Team
              </CardTitle>
              <p className="text-gray-400 text-sm mt-1">
                Chat med klient, holding, team leader og arbeidere
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Participants list */}
              <div className="flex flex-wrap gap-2 pb-3 border-b border-[#2a2a4a]">
                <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">
                  <User className="w-3 h-3 mr-1" />
                  Klient
                </Badge>
                <Badge className="bg-purple-500/20 text-purple-400">
                  <Building2 className="w-3 h-3 mr-1" />
                  Holding
                </Badge>
                <Badge className="bg-orange-500/20 text-orange-400">
                  <User className="w-3 h-3 mr-1" />
                  Basleder
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-400">
                  <Users className="w-3 h-3 mr-1" />
                  Arbeidere
                </Badge>
              </div>

              {/* Messages */}
              <div ref={teamChatContainerRef} className="h-80 overflow-y-auto space-y-3 p-3 bg-[#0f0f1a] rounded-lg">
                {teamMessages.length > 0 ? (
                  <>
                    {teamMessages.map((msg, idx) => {
                      const isOwnMessage = msg.sender_type === "holding_admin" || msg.sender_type === "platform_admin";
                      const senderColors = {
                        'client': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                        'client_admin': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                        'holding_admin': 'bg-purple-500/20 border-purple-500/30',
                        'platform_admin': 'bg-purple-500/20 border-purple-500/30',
                        'team_leader': 'bg-orange-500/20 border-orange-500/30',
                        'worker': 'bg-blue-500/20 border-blue-500/30'
                      };
                      const textColors = {
                        'client': 'text-[#23F7DD]',
                        'client_admin': 'text-[#23F7DD]',
                        'holding_admin': 'text-purple-400',
                        'platform_admin': 'text-purple-400',
                        'team_leader': 'text-orange-400',
                        'worker': 'text-blue-400'
                      };
                      const roleLabels = {
                        'client': 'Klient',
                        'client_admin': 'Klient',
                        'holding_admin': 'Holding',
                        'platform_admin': 'Admin',
                        'team_leader': 'Basleder',
                        'worker': 'Arbeider'
                      };
                      const bgColor = senderColors[msg.sender_type] || 'bg-gray-500/20 border-gray-500/30';
                      const txtColor = textColors[msg.sender_type] || 'text-gray-400';
                      const roleLabel = roleLabels[msg.sender_type] || msg.sender_type;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[80%] p-3 rounded-lg border ${bgColor}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${txtColor}`}>
                                {isOwnMessage ? 'Deg' : msg.sender_name}
                              </span>
                              <Badge variant="outline" className={`text-[10px] px-1 py-0 ${txtColor} border-current`}>
                                {roleLabel}
                              </Badge>
                            </div>
                            <p className="text-white text-sm">{msg.message}</p>
                            <p className="text-xs opacity-50 mt-1">
                              {msg.sent_at ? new Date(msg.sent_at).toLocaleString("no-NO", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit"
                              }) : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={teamMessagesEndRef} />
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <MessageSquare className="w-10 h-10 mx-auto text-gray-600 mb-2" />
                      <p className="text-gray-500 text-sm">Ingen meldinger ennå</p>
                      <p className="text-gray-600 text-xs mt-1">Start en samtale</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Send Message Form */}
              <div className="flex gap-2">
                <Textarea
                  value={newTeamMessage}
                  onChange={(e) => setNewTeamMessage(e.target.value)}
                  placeholder="Skriv en melding..."
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white resize-none flex-1"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendTeamMessage();
                    }
                  }}
                />
                <Button 
                  onClick={handleSendTeamMessage} 
                  disabled={sendingTeamMessage || !newTeamMessage.trim()} 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                >
                  {sendingTeamMessage ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => {
        setShowApproveDialog(open);
        if (!open) {
          setIsApprovalEdited(false); // Reset edit flag when closing
        }
      }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Godkjenn Prosjekt</DialogTitle>
            <DialogDescription className="text-gray-400">
              Sett endelig budsjett og betalingsvilkår for prosjektet.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Totalt budsjett (klient betaler)</Label>
                <Input
                  type="number"
                  value={approvalData.final_budget}
                  onChange={(e) => handleBudgetChange("final_budget", e.target.value)}
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Holding margin</Label>
                <Input
                  type="number"
                  value={approvalData.holding_margin}
                  onChange={(e) => handleBudgetChange("holding_margin", e.target.value)}
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                />
              </div>
            </div>
            
            {/* Breakdown */}
            <div className="p-4 bg-[#0f0f1a] rounded-lg space-y-3 border border-[#2a2a4a]">
              <h4 className="font-semibold text-white text-sm mb-2">Budsjettfordeling:</h4>
              
              {/* Platform fee */}
              {feeBreakdown && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">
                    Platform kommisjon ({feeBreakdown.platform_fee_percent || 2}%)
                  </span>
                  <span className="text-orange-400">
                    - {formatCurrency(feeBreakdown.platform_fee || (approvalData.final_budget * 0.02))}
                  </span>
                </div>
              )}
              
              {/* Holding margin */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">
                  Holding margin ({((approvalData.holding_margin / approvalData.final_budget) * 100).toFixed(1)}%)
                </span>
                <span className="text-blue-400">
                  - {formatCurrency(approvalData.holding_margin)}
                </span>
              </div>
              
              <div className="h-px bg-[#2a2a4a]"></div>
              
              {/* Team budget */}
              <div className="flex justify-between items-center">
                <span className="text-white font-semibold">Team budsjett (brut)</span>
                <span className="text-[#23F7DD] font-bold text-lg">
                  {formatCurrency(approvalData.team_budget)}
                </span>
              </div>
            </div>
            
            {/* Fee breakdown section */}
            {feeBreakdown && (
              <div className="p-4 bg-[#0f0f1a] rounded-lg space-y-2 border border-[#2a2a4a]">
                <h4 className="font-semibold text-white text-sm mb-2">Gebyrberegning (fra team budsjett):</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">AGA ({feeBreakdown.deductions?.aga_percent}%)</span>
                    <span className="text-gray-300">{formatCurrency(feeBreakdown.deductions?.aga)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Feriepenger ({feeBreakdown.deductions?.feriepenger_percent}%)</span>
                    <span className="text-gray-300">{formatCurrency(feeBreakdown.deductions?.feriepenger)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Andre kostnader ({feeBreakdown.deductions?.andre_kostnader_percent}%)</span>
                    <span className="text-gray-300">{formatCurrency(feeBreakdown.deductions?.andre_kostnader)}</span>
                  </div>
                  
                  <div className="h-px bg-[#2a2a4a] my-2"></div>
                  
                  <div className="flex justify-between font-semibold">
                    <span className="text-white">Netto til team</span>
                    <span className="text-green-400">{formatCurrency(feeBreakdown.net_to_team)}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-gray-400">Betalingsfrist (dager)</Label>
              <Input
                type="number"
                value={approvalData.payment_terms_days}
                onChange={(e) => {
                  setIsApprovalEdited(true);
                  setApprovalData(prev => ({
                    ...prev,
                    payment_terms_days: parseInt(e.target.value) || 14
                  }));
                }}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-400">Melding til klient (valgfritt)</Label>
              <Textarea
                value={approvalData.message}
                onChange={(e) => {
                  setIsApprovalEdited(true);
                  setApprovalData(prev => ({ ...prev, message: e.target.value }));
                }}
                placeholder="Eventuelle kommentarer til klienten..."
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowApproveDialog(false)}
              className="text-gray-400"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleApprove}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? "Godkjenner..." : "Godkjenn"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
          <DialogHeader>
            <DialogTitle>Avslå Prosjekt</DialogTitle>
            <DialogDescription className="text-gray-400">
              Oppgi grunn for avslaget.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Grunn for avslag..."
              className="bg-[#0f0f1a] border-[#2a2a4a] text-white min-h-[100px]"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowRejectDialog(false)}
              className="text-gray-400"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleReject}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing ? "Avslår..." : "Avslå Prosjekt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Counter-Offer Dialog */}
      <Dialog open={showCounterOfferDialog} onOpenChange={(open) => {
        setShowCounterOfferDialog(open);
        if (!open) {
          setIsCounterOfferEdited(false); // Reset edit flag when closing
        }
      }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-orange-400" />
              Send Mot-tilbud til Klient
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Foreslå et nytt budsjett til klienten. De kan akseptere eller avslå.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#0f0f1a] rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Klientens forslag</span>
                <span className="text-white font-bold">{formatCurrency(project?.proposed_budget)}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Ditt mot-tilbud (NOK)</Label>
              <Input
                type="number"
                value={counterOfferData.proposed_budget}
                onChange={(e) => {
                  setIsCounterOfferEdited(true);
                  setCounterOfferData({
                    ...counterOfferData,
                    proposed_budget: parseFloat(e.target.value) || 0
                  });
                }}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white text-lg"
                placeholder="Nytt budsjett..."
              />
              {counterOfferData.proposed_budget !== project?.proposed_budget && (
                <p className={`text-sm ${counterOfferData.proposed_budget > project?.proposed_budget ? "text-green-400" : "text-red-400"}`}>
                  {counterOfferData.proposed_budget > project?.proposed_budget 
                    ? `+${formatCurrency(counterOfferData.proposed_budget - project?.proposed_budget)} mer enn forslag`
                    : `${formatCurrency(counterOfferData.proposed_budget - project?.proposed_budget)} mindre enn forslag`
                  }
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Melding til klient (valgfritt)</Label>
              <Textarea
                value={counterOfferData.message}
                onChange={(e) => {
                  setIsCounterOfferEdited(true);
                  setCounterOfferData({
                    ...counterOfferData,
                    message: e.target.value
                  });
                }}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white resize-none"
                placeholder="Forklar hvorfor du foreslår dette budsjettet..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCounterOfferDialog(false)}
              className="text-gray-400"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSendCounterOffer}
              disabled={processing || !counterOfferData.proposed_budget}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {processing ? "Sender..." : "Send Mot-tilbud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Payment Breakdown Dialog */}
      <Dialog open={showPaymentBreakdownDialog} onOpenChange={setShowPaymentBreakdownDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-400" />
              Utbetalingsoversikt
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Detaljert oversikt over utbetalinger til teamet
            </DialogDescription>
          </DialogHeader>
          
          {paymentBreakdown && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#0d0d1a] rounded-lg">
                  <p className="text-gray-400 text-sm">Team Budsjett</p>
                  <p className="text-white font-semibold">{formatCurrency(paymentBreakdown.team_budget)}</p>
                </div>
                <div className="p-3 bg-[#0d0d1a] rounded-lg">
                  <p className="text-gray-400 text-sm">Netto til team</p>
                  <p className="text-[#23F7DD] font-semibold">{formatCurrency(paymentBreakdown.net_to_team)}</p>
                </div>
              </div>
              
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-gray-400 text-sm">Base andel per person ({paymentBreakdown.total_members} medlemmer)</p>
                <p className="text-purple-400 font-semibold">{formatCurrency(paymentBreakdown.base_share_per_member)}</p>
              </div>
              
              {/* Individual Payments */}
              <div className="space-y-3">
                <p className="text-gray-400 text-sm font-medium">Individuelle utbetalinger:</p>
                {paymentBreakdown.payments?.map((payment, idx) => (
                  <div key={idx} className="p-4 bg-[#0d0d1a] rounded-lg border border-[#2a2a4a]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={payment.role === "Team Leader" ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"}>
                          {payment.role}
                        </Badge>
                        <span className="text-white font-medium">{payment.name}</span>
                        <span className="text-gray-500 text-sm">({payment.worker_code})</span>
                      </div>
                      <span className="text-[#23F7DD] font-bold text-lg">
                        {formatCurrency(payment.total_payment)}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{payment.breakdown}</p>
                    {payment.tl_contribution_percent > 0 && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <Percent className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-300">
                          Gir {payment.tl_contribution_percent}% til TL = {formatCurrency(payment.tl_contribution_amount)}
                        </span>
                      </div>
                    )}
                    {payment.tl_bonus > 0 && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">
                          Mottar bonus fra workers: {formatCurrency(payment.tl_bonus)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Verification */}
              {paymentBreakdown.verification && (
                <div className={`p-3 rounded-lg ${paymentBreakdown.verification.match ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Verifikasjon</span>
                    {paymentBreakdown.verification.match ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Korrekt
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Uoverensstemmelse
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-gray-400">Sum utbetalinger:</span>
                    <span className="text-white">{formatCurrency(paymentBreakdown.verification.total_payments)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPaymentBreakdownDialog(false)}
              className="border-gray-500 text-gray-400"
            >
              Lukk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
